import { toUtcDateString } from "@/constants/timeRange";
import { parseAuditLogTimestamp } from "@/utils/auditLogs";
const DONUT_COLORS = [
    "#22d3ee",
    "#84cc16",
    "#a78bfa",
    "#f59e0b",
    "#f43f5e",
    "#14b8a6",
    "#3b82f6",
    "#ec4899",
    "#f97316",
    "#06b6d4",
    "#64748b"
];
function numberValue(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
function usageFromRequestSize(requestSize) {
    const value = numberValue(requestSize);
    return value > 0 ? value : 0;
}
function shortApiKey(apiKeyId) {
    return apiKeyId.length > 14 ? `${apiKeyId.slice(0, 14)}...` : apiKeyId;
}
function buildConicGradient(segments) {
    if (segments.length === 0) {
        return "conic-gradient(#e2e8f0 0deg 360deg)";
    }
    let current = 0;
    const stops = segments.map((segment)=>{
        const start = current;
        const end = current + segment.percentage * 3.6;
        current = end;
        return `${segment.color} ${start}deg ${end}deg`;
    });
    if (current < 360) {
        stops.push(`#e2e8f0 ${current}deg 360deg`);
    }
    return `conic-gradient(${stops.join(", ")})`;
}
function keyUsageTotalsFromMetered(apiKeyTotals) {
    const totals = apiKeyTotals ?? [];
    return totals.map((item)=>({
            apiKeyId: String(item["apiKeyId"] ?? item["ApiKeyId"] ?? ""),
            totalUsageValue: numberValue(item["totalUsageValue"] ?? item["TotalUsageValue"])
        })).filter((item)=>item.apiKeyId);
}
function keyUsageTotalsFromAuditLogs(auditLogs) {
    const totalsByApiKey = new Map();
    auditLogs.forEach((log)=>{
        if (!log.apiKeyId) {
            return;
        }
        const usageValue = usageFromRequestSize(log.requestSize);
        if (usageValue <= 0) {
            return;
        }
        totalsByApiKey.set(log.apiKeyId, (totalsByApiKey.get(log.apiKeyId) ?? 0) + usageValue);
    });
    return Array.from(totalsByApiKey, ([apiKeyId, totalUsageValue])=>({
            apiKeyId,
            totalUsageValue
        }));
}
function trendPointsFromUsageQuery({ usageData, selectedPeriod, startDate, endDate }) {
    if (!usageData) {
        return [];
    }
    if (selectedPeriod === "24h") {
        const hourlyRecords = usageData.hourlyUsage ?? [];
        const hourlyTotals = new Map();
        hourlyRecords.forEach((record)=>{
            const hourValue = record["hour"] ?? record["Hour"];
            const hourStart = typeof record["hourStartUtc"] === "string" ? record["hourStartUtc"] : typeof record["HourStartUtc"] === "string" ? record["HourStartUtc"] : undefined;
            let hour = typeof hourValue === "number" ? hourValue : undefined;
            if (hour === undefined && hourStart) {
                const parsedHour = new Date(hourStart);
                if (!Number.isNaN(parsedHour.getTime())) {
                    hour = parsedHour.getUTCHours();
                }
            }
            if (hour === undefined || hour < 0 || hour > 23) {
                return;
            }
            const usageValue = numberValue(record["usageValue"] ?? record["UsageValue"]);
            hourlyTotals.set(hour, (hourlyTotals.get(hour) ?? 0) + usageValue);
        });
        return Array.from({
            length: 24
        }, (_, hour)=>({
                timestamp: new Date(`${endDate}T${String(hour).padStart(2, "0")}:00:00Z`),
                label: `${String(hour).padStart(2, "0")}:00`,
                usageValue: hourlyTotals.get(hour) ?? 0
            }));
    }
    const dailyRecords = usageData.dailyUsage ?? [];
    const dailyTotals = new Map();
    dailyRecords.forEach((record)=>{
        const dateStringRaw = typeof record["date"] === "string" ? record["date"] : typeof record["DayUtc"] === "string" ? record["DayUtc"] : "";
        const dateString = dateStringRaw.slice(0, 10);
        if (!dateString) {
            return;
        }
        const usageValue = numberValue(record["totalUsageValue"] ?? record["TotalUsageValue"]);
        dailyTotals.set(dateString, (dailyTotals.get(dateString) ?? 0) + usageValue);
    });
    return buildDailyTrendPoints({
        dailyTotals,
        startDate,
        endDate
    });
}
function trendPointsFromAuditLogs({ auditLogs, selectedPeriod, startDate, endDate }) {
    if (selectedPeriod === "24h") {
        const hourlyTotals = new Map();
        auditLogs.forEach((log)=>{
            if (!log.requestTime) {
                return;
            }
            const parsed = parseAuditLogTimestamp(log.requestTime);
            if (Number.isNaN(parsed.getTime()) || toUtcDateString(parsed) !== endDate) {
                return;
            }
            const hour = parsed.getUTCHours();
            const usageValue = usageFromRequestSize(log.requestSize);
            hourlyTotals.set(hour, (hourlyTotals.get(hour) ?? 0) + usageValue);
        });
        return Array.from({
            length: 24
        }, (_, hour)=>({
                timestamp: new Date(`${endDate}T${String(hour).padStart(2, "0")}:00:00Z`),
                label: `${String(hour).padStart(2, "0")}:00`,
                usageValue: hourlyTotals.get(hour) ?? 0
            }));
    }
    const dailyTotals = new Map();
    auditLogs.forEach((log)=>{
        if (!log.requestTime) {
            return;
        }
        const parsed = parseAuditLogTimestamp(log.requestTime);
        if (Number.isNaN(parsed.getTime())) {
            return;
        }
        const dateString = toUtcDateString(parsed);
        if (dateString < startDate || dateString > endDate) {
            return;
        }
        const usageValue = usageFromRequestSize(log.requestSize);
        dailyTotals.set(dateString, (dailyTotals.get(dateString) ?? 0) + usageValue);
    });
    return buildDailyTrendPoints({
        dailyTotals,
        startDate,
        endDate
    });
}
function buildDailyTrendPoints({ dailyTotals, startDate, endDate }) {
    const points = [];
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    const cursor = new Date(start);
    while(cursor <= end){
        const dateString = toUtcDateString(cursor);
        points.push({
            timestamp: new Date(`${dateString}T00:00:00Z`),
            label: cursor.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                timeZone: "UTC"
            }),
            usageValue: dailyTotals.get(dateString) ?? 0
        });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return points;
}
export { DONUT_COLORS, buildConicGradient, keyUsageTotalsFromAuditLogs, keyUsageTotalsFromMetered, numberValue, shortApiKey, trendPointsFromAuditLogs, trendPointsFromUsageQuery, usageFromRequestSize };
